import React, { useEffect, useMemo, useState } from 'react';

/**
 * Check off roster names during Roll Call.
 */
export default function RollCallModal({
  open,
  roster = [],
  initialPresentIds = null,
  onCancel,
  onConfirm,
}) {
  const active = useMemo(
    () => (roster || []).filter((r) => r.isActive !== false),
    [roster]
  );

  const [present, setPresent] = useState(() => new Set());

  useEffect(() => {
    if (!open) return;
    if (Array.isArray(initialPresentIds) && initialPresentIds.length) {
      setPresent(new Set(initialPresentIds));
    } else {
      setPresent(new Set(active.map((r) => r.id)));
    }
  }, [open, active, initialPresentIds]);

  if (!open) return null;

  const toggle = (id) => {
    setPresent((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const presentCount = present.size;
  const absentCount = Math.max(0, active.length - presentCount);

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="roll-call-title"
        className="max-h-[90vh] w-full max-w-md overflow-hidden rounded-2xl border border-pp-line bg-pp-surface shadow-pp"
      >
        <div className="border-b border-pp-line px-5 py-4">
          <h2 id="roll-call-title" className="text-lg font-semibold tracking-tight text-pp-ink">
            Roll call
          </h2>
          <p className="mt-1 text-sm text-pp-muted">
            Check names as they respond. Present: {presentCount} · Absent: {absentCount}
          </p>
        </div>
        <div className="max-h-[50vh] space-y-1 overflow-y-auto px-5 py-3">
          {active.length === 0 ? (
            <p className="text-sm text-pp-muted">
              No active roster members. Add people under Administration → Member roster.
            </p>
          ) : (
            active.map((row) => (
              <label
                key={row.id}
                className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2 hover:bg-pp-paper"
              >
                <input
                  type="checkbox"
                  checked={present.has(row.id)}
                  onChange={() => toggle(row.id)}
                />
                <span className="text-sm font-medium text-pp-ink">{row.displayName}</span>
              </label>
            ))
          )}
        </div>
        <div className="flex justify-end gap-2 border-t border-pp-line px-5 py-4">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-xl border border-pp-line px-4 py-2 text-sm font-medium text-pp-muted hover:bg-pp-paper"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={active.length === 0}
            onClick={() =>
              onConfirm({
                presentIds: [...present],
                presentCount,
                absentCount,
                totalCount: active.length,
                attendance: active.map((r) => ({
                  rosterId: r.id,
                  name: r.displayName,
                  present: present.has(r.id),
                })),
              })
            }
            className="rounded-xl bg-pp-accent px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            Confirm roll call
          </button>
        </div>
      </div>
    </div>
  );
}
